/** Breadcrumb navigation for the current storage folder path */

import { ChevronRight, HardDrive } from 'lucide-react'
import { useT } from '../../i18n'
import type { StorageFolderNode } from '../../hooks/use-storage'
import { getFolderPath } from './storage-tree-utils'

interface StorageBreadcrumbProps {
  folders: StorageFolderNode[]
  activeFolderId: string | null
  onSelect: (id: string | null) => void
}

const linkClass = 'cursor-pointer rounded px-1 py-0.5 text-fg-muted hover:bg-surface-hover hover:text-fg'

export function StorageBreadcrumb({ folders, activeFolderId, onSelect }: StorageBreadcrumbProps) {
  const t = useT()
  const path = getFolderPath(folders, activeFolderId)

  return (
    <nav aria-label={t('storage.path')} className="min-w-0">
      <ol className="flex flex-wrap items-center gap-1 text-sm">
        <li>
          {activeFolderId === null ? (
            <span className="flex items-center gap-1.5 px-1 py-0.5 font-semibold text-fg" aria-current="page">
              <HardDrive className="h-4 w-4" aria-hidden="true" />
              {t('storage.root')}
            </span>
          ) : (
            <button type="button" onClick={() => onSelect(null)} className={`flex items-center gap-1.5 ${linkClass}`}>
              <HardDrive className="h-4 w-4" aria-hidden="true" />
              {t('storage.root')}
            </button>
          )}
        </li>
        {path.map((segment, i) => {
          const isLast = i === path.length - 1
          return (
            <li key={segment.id} className="flex min-w-0 items-center gap-1">
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden="true" />
              {isLast ? (
                <span className="truncate px-1 py-0.5 font-semibold text-fg" aria-current="page">
                  {segment.name}
                </span>
              ) : (
                <button type="button" onClick={() => onSelect(segment.id)} className={`truncate ${linkClass}`}>
                  {segment.name}
                </button>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
